import { statsCache, getTierForDate, TIER_CONFIGS } from '../utils/statsCache';
import { statsDataService } from '../modules/stats/dataService';

export class SilentRefreshService {
  private static instance: SilentRefreshService;
  private intervalId: number | null = null;

  private readonly CACHE_PREFIX = 'avalon_stats:';
  private readonly CHECK_INTERVAL = 30_000; // 30s
  private readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // 5m

  private refreshingKeys = new Set<string>();

  public static getInstance(): SilentRefreshService {
    if (!SilentRefreshService.instance) {
      SilentRefreshService.instance = new SilentRefreshService();
    }
    return SilentRefreshService.instance;
  }

  public start(): void {
    if (this.intervalId) {
      console.log('[SilentRefresh] already running');
      return;
    }

    console.log('[SilentRefresh] started');
    this.intervalId = window.setInterval(() => {
      if (!document.hidden) {
        void this.checkAndRefresh();
      }
    }, this.CHECK_INTERVAL);
  }

  public stop(): void {
    if (!this.intervalId) return;

    window.clearInterval(this.intervalId);
    this.intervalId = null;
    console.log('[SilentRefresh] stopped');
  }

  private async checkAndRefresh(): Promise<void> {
    try {
      const keys = this.getAllCacheKeys();
      for (const key of keys) {
        const shouldRefresh = await this.shouldRefreshKey(key);
        if (shouldRefresh && !this.refreshingKeys.has(key)) {
          void this.refreshInBackground(key);
        }
      }
    } catch (error) {
      console.warn('[SilentRefresh] check failed', error);
    }
  }

  private getAllCacheKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.CACHE_PREFIX)) {
        keys.push(key);
      }
    }
    return keys;
  }

  private toLogicalCacheKey(storageKey: string): string {
    let logical = storageKey.startsWith(this.CACHE_PREFIX)
      ? storageKey.slice(this.CACHE_PREFIX.length)
      : storageKey;
    logical = logical.replace(/:v\d+(?:\.\d+)*/, '');
    return logical;
  }

  private async shouldRefreshKey(storageKey: string): Promise<boolean> {
    try {
      const logicalKey = this.toLogicalCacheKey(storageKey);
      const item = localStorage.getItem(storageKey);
      if (!item) return false;

      const entry = JSON.parse(item);
      if (!entry || !entry.timestamp || !entry.data) return false;

      const dateStr = this.extractDateFromKey(logicalKey);
      if (!dateStr) return false;

      const tier = getTierForDate(dateStr);
      if (tier === 'cold') return false;

      const config = TIER_CONFIGS[tier];
      const expiresAt = entry.timestamp + config.ttl;
      const timeToExpiry = expiresAt - Date.now();
      const shouldRefresh = timeToExpiry > 0 && timeToExpiry < this.REFRESH_THRESHOLD;

      if (shouldRefresh) {
        console.log(
          `[SilentRefresh] key ${logicalKey} expires in ${Math.floor(timeToExpiry / 1000)}s, refreshing`
        );
      }

      return shouldRefresh;
    } catch (error) {
      console.warn('[SilentRefresh] shouldRefreshKey failed', storageKey, error);
      return false;
    }
  }

  private extractDateFromKey(key: string): string | null {
    const dateMatch = key.match(/(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) return dateMatch[1];
    return new Date().toISOString().split('T')[0];
  }

  private async refreshInBackground(storageKey: string): Promise<void> {
    this.refreshingKeys.add(storageKey);

    try {
      const logicalKey = this.toLogicalCacheKey(storageKey);
      let newData: unknown = null;

      if (logicalKey.startsWith('stats_overview_')) {
        const dateStr = this.extractDateFromKey(logicalKey);
        if (dateStr) {
          newData = await statsDataService.loadPlayerStatsWithDateRange(
            undefined,
            undefined,
            new Date(dateStr),
            new Date(dateStr)
          );
        }
      } else if (logicalKey.startsWith('player_stats:')) {
        const parts = logicalKey.split(':');
        const dojoFilter = parts[1] || 'all';
        const seasonFilter = parts[2] || 'all';
        newData = await statsDataService.loadPlayerStats(dojoFilter, seasonFilter);
      }

      if (newData) {
        const dateStr = this.extractDateFromKey(logicalKey);
        await statsCache.set(logicalKey, newData, dateStr || undefined);
        console.log(`[SilentRefresh] refreshed ${logicalKey}`);
      }
    } catch (error) {
      console.warn('[SilentRefresh] refresh failed', storageKey, error);
    } finally {
      this.refreshingKeys.delete(storageKey);
    }
  }
}

